import type { NavigateFunction } from 'react-router-dom';
import { deviceClock } from '../../utils/booking';

/**
 * "You just missed it" (docs/plans/CIRCLE-STARTING-NOTICE.md N14): anyone who
 * reaches the hourly circle after T lands on the Circles hub with a note above
 * the next circle's card and its normal Sign up button. Never an automatic
 * signup: they chose the earlier circle, not this one.
 *
 * Carried in router state (never a URL), so it shows once, on the navigation
 * that brought them. Used by the starting-soon notice's Join (409 `missed`)
 * and, in #4, by the pending join after verification.
 */
export interface JustMissedState {
  tab: 'circles';
  justMissed: { nextAt: string | null };
}

export const HUB_PATH = '/app/empathy';

export function goJustMissed(navigate: NavigateFunction, nextAt: string | null): void {
  const state: JustMissedState = { tab: 'circles', justMissed: { nextAt } };
  void navigate(HUB_PATH, { state });
}

/** The note's next-circle time from router state, or undefined when not arriving late. */
export function justMissedFrom(state: unknown): { nextAt: string | null } | undefined {
  const jm = (state as { justMissed?: { nextAt?: unknown } } | null)?.justMissed;
  if (!jm || typeof jm !== 'object') return undefined;
  return { nextAt: typeof jm.nextAt === 'string' ? jm.nextAt : null };
}

/** "You just missed it. The next one is at 8:00 pm MDT." (device zone, named; no dashes) */
export function justMissedCopy(nextAt: string | null): string {
  if (!nextAt) return 'You just missed it. The next one starts at the top of the hour.';
  const t = new Date(nextAt);
  if (Number.isNaN(t.getTime()))
    return 'You just missed it. The next one starts at the top of the hour.';
  return `You just missed it. The next one is at ${deviceClock(t)}.`;
}
